import { type Controller, createController } from './controller';
import { checkGate, clearActivation, storeActivation } from './gate';
import { createHost } from './host';
import { getMessages } from './i18n';
import { validateReviewerKey } from './transport';
import { type FeedbackController, type FeedbackOptions, resolveOptions } from './types';

export type { FeedbackController, FeedbackOptions, Locale } from './types';

declare global {
  interface Window {
    /**
     * Set while a widget instance is mounted. Lives on `window` (not module
     * state) because two copies of the IIFE bundle on one page — a stray
     * duplicate <script> tag — don't share module scope, only the page.
     */
    __bytaxFeedbackMounted?: boolean;
  }
}

const NOOP_CONTROLLER: FeedbackController = {
  destroy() {},
  isMounted: () => false,
};

/** Resolves once the DOM is ready to receive the host element. */
function waitForDom(): Promise<void> {
  if (document.readyState !== 'loading') return Promise.resolve();
  return new Promise((resolve) => {
    document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
  });
}

export function initFeedback(options: FeedbackOptions): FeedbackController {
  if (typeof window === 'undefined') {
    return NOOP_CONTROLLER;
  }

  if (window.__bytaxFeedbackMounted) {
    console.warn('[bytax-feedback] initFeedback() called while already mounted — ignoring');
    return NOOP_CONTROLLER;
  }
  window.__bytaxFeedbackMounted = true;

  const resolved = resolveOptions(options);

  let destroyed = false;
  let controller: Controller | null = null;

  /**
   * Single owner of instance teardown: the public destroy() and the internal
   * invalid-revalidation path both come through here, so `destroyed` and the
   * window-level singleton flag can never diverge. The `destroyed` guard also
   * keeps a late async teardown from clearing a flag that a newer instance
   * (mounted after this one was destroyed) now owns.
   */
  function teardown(): void {
    if (destroyed) return;
    destroyed = true;
    controller?.destroy();
    controller = null;
    window.__bytaxFeedbackMounted = false;
  }

  (async () => {
    const gate = await checkGate(resolved);
    if (!gate || destroyed) return;

    await waitForDom();
    if (destroyed) return;

    const host = createHost(resolved.zIndex);
    controller = createController({
      host,
      options: resolved,
      messages: getMessages(resolved.locale),
      reviewerKey: gate.reviewerKey,
      defaultEmail: gate.defaultEmail,
    });

    if (gate.needsRevalidation) {
      // Stored activations mount instantly; a revoked key is caught here.
      const result = await validateReviewerKey(resolved.endpoint, resolved.token, gate.reviewerKey);
      if (result.verdict === 'invalid') {
        clearActivation(resolved.token);
        // Full teardown, not just an unmount: the singleton flag must be
        // released too, or a later initFeedback() on this page would
        // warn-and-noop forever despite nothing being mounted.
        teardown();
      } else if (result.verdict === 'valid') {
        // Refresh both the stored copy and the live instance so the next
        // dialog open (and the next page load) use the current
        // admin-configured default — including clearing a removed one.
        // A transport 'error' keeps the stale stored value instead, the same
        // fail-open posture the verdict itself gets.
        storeActivation(resolved.token, gate.reviewerKey, result.defaultReporterEmail);
        controller?.setDefaultEmail(result.defaultReporterEmail);
      }
    }
  })().catch(console.warn);

  return {
    destroy: teardown,
    isMounted: () => controller !== null,
  };
}
